import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Database, Loader2 } from "lucide-react";
import api from "../lib/api";

function HealthBadge({ health }) {
  const active = health === "ACTIVE";
  return (
    <span
      className={`text-[10px] px-2 py-0.5 rounded-full border whitespace-nowrap ${
        active
          ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30"
          : "bg-orange-500/10 text-orange-500 border-orange-500/30"
      }`}
      data-testid="source-health"
    >
      {health}
    </span>
  );
}

export default function DataSources() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "ARQITEK — Data Sources";
    api.get("/acq/data-sources").then((r) => setSources(r.data.sources || [])).finally(() => setLoading(false));
  }, []);

  const activeCount = sources.filter((s) => s.health === "ACTIVE").length;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="data-sources-page">
      <Link to="/ai-office" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="ds-back">
        <ArrowLeft className="h-4 w-4" /> AI Office
      </Link>
      <div>
        <span className="block text-xs uppercase tracking-[0.15em] text-primary mb-2">Acquisition Intelligence</span>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Data Sources</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Every feed the agents rely on. Arqitek never pretends to have access it doesn't — sources without a
          legal or technical path show <span className="text-foreground font-medium">MANUAL / INTEGRATION REQUIRED</span> and return zero results.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm py-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading sources…</div>
      ) : (
        <>
          <div className="text-sm text-muted-foreground">{activeCount} of {sources.length} sources connected</div>
          <div className="space-y-3">
            {sources.map((s, i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-4" data-testid={`source-card-${i}`}>
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Database className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{s.name}</span>
                      <HealthBadge health={s.health} />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 mt-2 text-xs">
                      <div><span className="text-muted-foreground">Type: </span>{s.type}</div>
                      <div><span className="text-muted-foreground">Jurisdictions: </span>{s.jurisdictions}</div>
                      <div><span className="text-muted-foreground">Auth: </span>{s.auth}</div>
                      <div><span className="text-muted-foreground">Cost: </span>{s.cost}</div>
                    </div>
                    {s.note && <div className="text-[11px] text-muted-foreground mt-1">{s.note}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
